import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormNumberInput } from "@/components/companies/shared/autosave-form/AutosaveFormNumberInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeBoolean } from "@/components/companies/shared/IntakeBoolean";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyBooleanField, ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { nullableNonOptionalNumberSchema } from "@/lib/schemas";
import { JobIntake } from "@/types";

const talentRecruitingHistorySchema = z.object({
  openDuration: z.string().nullable(),
  fillTimeUsually: z.string().nullable(),
  areOtherStaffingFirmsWorking: z.boolean(),
  willOtherStaffingFirmsWork: z.boolean(),
  isInternalTeamRecruiting: z.boolean(),
  struggles: z.string().nullable(),
  numberOfCandidatesSubmitted: nullableNonOptionalNumberSchema,
  numberOfInterviews: nullableNonOptionalNumberSchema,
  extraContext: z.string().nullable(),
  customersFrustrations: z.string().nullable(),
});

export const JobTalentRecruitingHistory = ({
  isEditing,
  jobIntake,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  /* Misc Hooks */
  const history = jobIntake.talentRecruitingHistory;
  const form = useForm<z.infer<typeof talentRecruitingHistorySchema>>({
    resolver: zodResolver(talentRecruitingHistorySchema),
    defaultValues: {
      openDuration: history.openDuration ?? null,
      fillTimeUsually: history.fillTimeUsually ?? null,
      areOtherStaffingFirmsWorking: history.areOtherStaffingFirmsWorking,
      willOtherStaffingFirmsWork: history.willOtherStaffingFirmsWork,
      isInternalTeamRecruiting: history.isInternalTeamRecruiting,
      struggles: history.struggles ?? null,
      numberOfCandidatesSubmitted: history.numberOfCandidatesSubmitted ?? null,
      numberOfInterviews: history.numberOfInterviews ?? null,
      extraContext: history.extraContext ?? null,
      customersFrustrations: history.customersFrustrations ?? null,
    },
  });

  const areOtherStaffingFirmsWorking = form.watch(
    "areOtherStaffingFirmsWorking",
  );
  const willOtherStaffingFirmsWork = form.watch("willOtherStaffingFirmsWork");
  const isInternalTeamRecruiting = form.watch("isInternalTeamRecruiting");

  /* Handlers */
  const handleSave = (data: z.infer<typeof talentRecruitingHistorySchema>) => {
    updateJobIntake({
      updateJobIntakeRequest: { talentRecruitingHistory: data },
    });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  return (
    <Column className="gap-6">
      {isEditing ? (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <IntakeBoolean
              checked={areOtherStaffingFirmsWorking}
              className="flex-1"
              label="Are there other staffing firms currently working on this job?"
              onToggle={() => {
                form.setValue(
                  "areOtherStaffingFirmsWorking",
                  !areOtherStaffingFirmsWorking,
                );
                saveOnBlur();
              }}
            />
            <IntakeBoolean
              checked={willOtherStaffingFirmsWork}
              className="flex-1"
              label="Are they planning on having other staffing firms work on this job?"
              onToggle={() => {
                form.setValue(
                  "willOtherStaffingFirmsWork",
                  !willOtherStaffingFirmsWork,
                );
                saveOnBlur();
              }}
            />
            <IntakeBoolean
              checked={isInternalTeamRecruiting}
              className="flex-1"
              label="Is their internal recruiting team trying to fill this job?"
              onToggle={() => {
                form.setValue(
                  "isInternalTeamRecruiting",
                  !isInternalTeamRecruiting,
                );
                saveOnBlur();
              }}
            />

            <Row className="gap-4">
              <AutosaveFormTextInput
                className="flex-1"
                fieldName="openDuration"
                form={form}
                label="How Long Has Role Been Open"
                onBlur={saveOnBlur}
                placeholder="e.g., 3 months"
              />
              <AutosaveFormTextInput
                className="flex-1"
                fieldName="fillTimeUsually"
                form={form}
                label="How Long Does It Usually Take to Fill"
                onBlur={saveOnBlur}
                placeholder="e.g., 4-6 weeks"
              />
            </Row>

            <Row className="gap-4">
              <AutosaveFormNumberInput
                className="flex-1"
                fieldName="numberOfCandidatesSubmitted"
                form={form}
                label="Candidates Submitted"
                onBlur={saveOnBlur}
                placeholder="e.g., 15"
              />
              <AutosaveFormNumberInput
                className="flex-1"
                fieldName="numberOfInterviews"
                form={form}
                label="Interviews Held"
                onBlur={saveOnBlur}
                placeholder="e.g., 5"
              />
            </Row>

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="struggles"
              form={form}
              infoTooltip={`What's been the biggest challenge filling this role — high turnover, a string of bad hires, lack of quality from other agencies?\nWhy is the client open to outside help on this one?`}
              label="Struggles"
              onBlur={saveOnBlur}
            />
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="customersFrustrations"
              form={form}
              infoTooltip={`How frustrated is the client with the hiring process for this role?\nAre they skeptical of staffing agencies at this point, and how might that affect urgency on our end?`}
              label="Customer Frustrations"
              onBlur={saveOnBlur}
            />
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="extraContext"
              form={form}
              infoTooltip={`Is there anything else the client has shared about hiring for this job that would be helpful for our team to know?`}
              label="Extra Context"
              onBlur={saveOnBlur}
            />

            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <ReadOnlyBooleanField
            label="Other Staffing Firms Working"
            isSelected={history.areOtherStaffingFirmsWorking}
          />
          <ReadOnlyBooleanField
            label="Will Other Staffing Firms Work"
            isSelected={history.willOtherStaffingFirmsWork}
          />
          <ReadOnlyBooleanField
            label="Internal Team Recruiting"
            isSelected={history.isInternalTeamRecruiting}
          />
          <ReadOnlyField
            label="How Long Has Role Been Open"
            value={history.openDuration ?? null}
          />
          <ReadOnlyField
            label="How Long Does It Usually Take to Fill"
            value={history.fillTimeUsually ?? null}
          />
          <div />
          <ReadOnlyField
            label="Candidates Submitted"
            value={
              history.numberOfCandidatesSubmitted != null
                ? String(history.numberOfCandidatesSubmitted)
                : null
            }
          />
          <ReadOnlyField
            label="Interviews Held"
            value={
              history.numberOfInterviews != null
                ? String(history.numberOfInterviews)
                : null
            }
          />
          <div />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Struggles"
            value={history.struggles ?? null}
          />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Customer Frustrations"
            value={history.customersFrustrations ?? null}
          />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Extra Context"
            value={history.extraContext ?? null}
          />
        </div>
      )}
    </Column>
  );
};
